import { useMemo, useState, useRef, useEffect } from 'react';
import { ChevronDown, FolderIcon, Pencil } from 'lucide-react';
import { Droppable, Draggable } from '@hello-pangea/dnd';
import { Task, Tag, Folder } from '@/types/task';
import { TaskItem } from '@/components/TaskItem';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

interface FolderGroup {
  folder: Folder | null; // null = "Unfiled"
  tasks: Task[];
}

interface FolderGroupedListProps {
  tasks: Task[];
  folders: Folder[];
  tags: Tag[];
  droppablePrefix: string;
  isDraggingFromBacklog?: boolean;
  onToggleTask: (id: string) => void;
  onDeleteTask: (id: string) => void;
  onUpdateTask: (id: string, updates: Partial<Task>) => void;
  onMoveToBacklog?: (id: string) => void;
  onCreateTag?: (name: string, color: string) => Promise<Tag | null>;
  onCreateFolder?: (name: string, color: string) => Promise<Folder | null>;
  onUpdateFolder?: (id: string, updates: Partial<Pick<Folder, 'name' | 'color'>>) => Promise<void>;
}

export function FolderGroupedList({
  tasks,
  folders,
  tags,
  droppablePrefix,
  isDraggingFromBacklog = false,
  onToggleTask,
  onDeleteTask,
  onUpdateTask,
  onMoveToBacklog,
  onCreateTag,
  onCreateFolder,
  onUpdateFolder,
}: FolderGroupedListProps) {
  // Persist collapsed state per folder
  const [collapsedIds, setCollapsedIds] = useState<Set<string>>(new Set());

  const groups: FolderGroup[] = useMemo(() => {
    const folderMap = new Map<string | null, Task[]>();

    // Initialize all folder groups (even empty ones so headers always show)
    for (const folder of folders) {
      folderMap.set(folder.id, []);
    }
    folderMap.set(null, []);

    for (const task of tasks) {
      const key = task.folder_id;
      if (!folderMap.has(key)) {
        // folder_id references a deleted folder; treat as unfiled
        folderMap.get(null)!.push(task);
      } else {
        folderMap.get(key)!.push(task);
      }
    }

    const result: FolderGroup[] = [];

    // Folders in order first
    for (const folder of folders) {
      const folderTasks = folderMap.get(folder.id) || [];
      if (folderTasks.length > 0) {
        result.push({ folder, tasks: folderTasks });
      }
    }

    // Unfiled last
    const unfiled = folderMap.get(null) || [];
    if (unfiled.length > 0) {
      result.push({ folder: null, tasks: unfiled });
    }

    return result;
  }, [tasks, folders]);

  const toggleCollapse = (id: string) => {
    setCollapsedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Track a running draggable index across all groups for correct drag ordering
  let globalIndex = 0;

  if (groups.length === 0) return null;

  // If only one group (e.g. all unfiled), skip folder headers
  if (groups.length === 1 && groups[0].folder === null) {
    return (
      <Droppable droppableId={`${droppablePrefix}-tasks`} isDropDisabled={isDraggingFromBacklog}>
        {(provided, snapshot) => (
          <div
            {...provided.droppableProps}
            ref={provided.innerRef}
            className={cn(
              "space-y-2 min-h-[50px] rounded-lg transition-colors",
              snapshot.isDraggingOver && "bg-primary/5"
            )}
          >
            {groups[0].tasks.map((task, index) => (
              <Draggable key={task.id} draggableId={task.id} index={index}>
                {(dragProvided, dragSnapshot) => {
                  const getTransition = () => {
                    if (dragSnapshot.isDragging && !dragSnapshot.isDropAnimating) return 'none';
                    if (dragSnapshot.isDropAnimating) return undefined;
                    return 'transform 0.2s cubic-bezier(0.2, 0, 0, 1)';
                  };
                  return (
                    <div
                      ref={dragProvided.innerRef}
                      {...dragProvided.draggableProps}
                      style={{ ...dragProvided.draggableProps.style, transition: getTransition() }}
                    >
                      <TaskItem
                        task={task}
                        tags={tags}
                        folders={folders}
                        onToggle={onToggleTask}
                        onDelete={onDeleteTask}
                        onUpdateTask={onUpdateTask}
                        onMoveToBacklog={onMoveToBacklog}
                        onCreateTag={onCreateTag}
                        onCreateFolder={onCreateFolder}
                        isDragging={dragSnapshot.isDragging}
                        dragHandleProps={dragProvided.dragHandleProps || undefined}
                      />
                    </div>
                  );
                }}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    );
  }

  return (
    <div className="space-y-4">
      {groups.map((group) => {
        const groupId = group.folder?.id ?? 'unfiled';
        const isOpen = !collapsedIds.has(groupId);
        const startIndex = globalIndex;
        globalIndex += group.tasks.length;

        return (
          <FolderSection
            key={groupId}
            group={group}
            isOpen={isOpen}
            onToggle={() => toggleCollapse(groupId)}
            droppableId={`${droppablePrefix}-folder-${groupId}`}
            isDraggingFromBacklog={isDraggingFromBacklog}
            startIndex={startIndex}
            tags={tags}
            folders={folders}
            onToggleTask={onToggleTask}
            onDeleteTask={onDeleteTask}
            onUpdateTask={onUpdateTask}
            onMoveToBacklog={onMoveToBacklog}
            onCreateTag={onCreateTag}
            onCreateFolder={onCreateFolder}
            onUpdateFolder={onUpdateFolder}
          />
        );
      })}
    </div>
  );
}

function FolderSection({
  group,
  isOpen,
  onToggle,
  droppableId,
  isDraggingFromBacklog,
  startIndex,
  tags,
  folders,
  onToggleTask,
  onDeleteTask,
  onUpdateTask,
  onMoveToBacklog,
  onCreateTag,
  onCreateFolder,
  onUpdateFolder,
}: {
  group: FolderGroup;
  isOpen: boolean;
  onToggle: () => void;
  droppableId: string;
  isDraggingFromBacklog: boolean;
  startIndex: number;
  tags: Tag[];
  folders: Folder[];
  onToggleTask: (id: string) => void;
  onDeleteTask: (id: string) => void;
  onUpdateTask: (id: string, updates: Partial<Task>) => void;
  onMoveToBacklog?: (id: string) => void;
  onCreateTag?: (name: string, color: string) => Promise<Tag | null>;
  onCreateFolder?: (name: string, color: string) => Promise<Folder | null>;
  onUpdateFolder?: (id: string, updates: Partial<Pick<Folder, 'name' | 'color'>>) => Promise<void>;
}) {
  const folder = group.folder;
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(folder?.name ?? '');
  const renameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isRenaming && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [isRenaming]);

  const handleRenameSave = () => {
    if (folder && renameValue.trim() && renameValue.trim() !== folder.name && onUpdateFolder) {
      onUpdateFolder(folder.id, { name: renameValue.trim() });
    } else {
      setRenameValue(folder?.name ?? '');
    }
    setIsRenaming(false);
  };

  const handleRenameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleRenameSave();
    else if (e.key === 'Escape') {
      setRenameValue(folder?.name ?? '');
      setIsRenaming(false);
    }
  };

  return (
    <Collapsible open={isOpen} onOpenChange={onToggle}>
      <div className="flex items-center gap-2 py-2 group/folder-header">
        <CollapsibleTrigger className="flex items-center gap-2 flex-1 text-left">
          <ChevronDown
            className={cn(
              "h-4 w-4 text-muted-foreground transition-transform duration-200",
              !isOpen && "-rotate-90"
            )}
          />
          {folder ? (
            <span
              className="w-2.5 h-2.5 rounded-full flex-shrink-0"
              style={{ backgroundColor: folder.color }}
            />
          ) : (
            <FolderIcon className="h-3.5 w-3.5 text-muted-foreground" />
          )}
          {isRenaming ? (
            <Input
              ref={renameInputRef}
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onBlur={handleRenameSave}
              onKeyDown={handleRenameKeyDown}
              onClick={(e) => e.stopPropagation()}
              className="h-6 text-sm py-0 px-1 w-32"
            />
          ) : (
            <span className="text-sm font-medium text-muted-foreground">
              {folder ? folder.name : 'Unfiled'}
            </span>
          )}
          <span className="text-xs text-muted-foreground/60 ml-1">
            {group.tasks.length}
          </span>
        </CollapsibleTrigger>
        {folder && onUpdateFolder && !isRenaming && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setRenameValue(folder.name);
              setIsRenaming(true);
            }}
            className="opacity-100 sm:opacity-0 sm:group-hover/folder-header:opacity-100 transition-opacity p-1 rounded hover:bg-muted text-muted-foreground"
            aria-label="Rename folder"
          >
            <Pencil className="h-3 w-3" />
          </button>
        )}
      </div>
      <CollapsibleContent className="pt-1">
        <Droppable droppableId={droppableId} isDropDisabled={isDraggingFromBacklog}>
          {(provided, snapshot) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              className={cn(
                "space-y-2 min-h-[30px] rounded-lg transition-colors pl-5",
                snapshot.isDraggingOver && "bg-primary/5"
              )}
            >
              {group.tasks.map((task, index) => (
                <Draggable key={task.id} draggableId={task.id} index={startIndex + index}>
                  {(dragProvided, dragSnapshot) => {
                    const getTransition = () => {
                      if (dragSnapshot.isDragging && !dragSnapshot.isDropAnimating) return 'none';
                      if (dragSnapshot.isDropAnimating) return undefined;
                      return 'transform 0.2s cubic-bezier(0.2, 0, 0, 1)';
                    };
                    return (
                      <div
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        style={{ ...dragProvided.draggableProps.style, transition: getTransition() }}
                      >
                        <TaskItem
                          task={task}
                          tags={tags}
                          folders={folders}
                          onToggle={onToggleTask}
                          onDelete={onDeleteTask}
                          onUpdateTask={onUpdateTask}
                          onMoveToBacklog={onMoveToBacklog}
                          onCreateTag={onCreateTag}
                          onCreateFolder={onCreateFolder}
                          isDragging={dragSnapshot.isDragging}
                          dragHandleProps={dragProvided.dragHandleProps || undefined}
                        />
                      </div>
                    );
                  }}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </CollapsibleContent>
    </Collapsible>
  );
}
